<template>
  <div class="playlist">
    <div class="pgnnp-header">
      <Paganiniplus />
    </div>
    <div class="mk-container">
      <Breadcrumbs :breadcrumbs="breadcrumbs" />
      <Transition name="fade" mode="out-in">
        <div v-if="loaded && playlist" class="container--playlist">
          <div v-if="playlist" class="info">
            <div :style="coverBackgroundImage" class="cover">
              <!--  -->
            </div>
            <div class="box">
              <div class="subject">
                <a v-if="isMe" class="icon-btn ctrl-btn medium-btn large-btn" title="edit-btn" @click="openEditing">
                  <i class="icon material-icons">
                    edit
                  </i>
                </a>
                <h1 class="title">
                  {{ displayLocaleTitle(playlist) }}
                </h1>
              </div>
              <div v-show="playlist && playlist.user && playlist.user.id" class="created">
                <RouterLink :to="{name: 'memberPage', params: { locale: $i18n.localePath(), id: playlist.user.id }}" title="avatar" class="avatar">
                  <img v-img-loader :data-src="playlist.user.avatar"
                       :data-default-src="'/img/img-default/img-user-default-s.png'"
                       :title="playlist.user.name" alt="user avatar"
                  >
                </RouterLink>
                <div class="text">
                  {{ $t('PLAYLIST.CREATED_BY', {member: playlist.user.name}) }}
                </div>
              </div>
              <div v-show="playlist && playlist.tracks && playlist.tracks.length > 0" class="control">
                <a :class="{'disabled': pgnnpAudioMode}" class="play-btn" title="play-btn" data-cy="playBtn" @click="!pgnnpAudioMode ? setPlaylistToQueue() : null">
                  <PlayerStaticPlayBtn :width="clientWidth < 768 ? 50 : 77" :height="clientWidth < 768 ? 50 : 77" :disabled="pgnnpAudioMode" />
                </a>
                <div class="btn-list">
                  <a class="icon-btn ctrl-btn small-btn medium-btn" title="save-btn" @click="toggleFollowing">
                    <template v-if="!followingUpdate">
                      <i v-if="isFollowing" class="icon material-icons">
                        favorite
                      </i>
                      <i v-else class="icon material-icons ">
                        favorite_border
                      </i>
                    </template>
                    <template v-if="followingUpdate">
                      <Loader :width="40" :height="40" />
                      <!-- <i class="icon material-icons beat">favorite</i> -->
                    </template>
                  </a>
                  <a v-if="isMe" class="icon-btn ctrl-btn small-btn" title="edit-small-btn" @click="openEditing">
                    <i class="icon material-icons">
                      edit
                    </i>
                  </a>
                  <a class="icon-btn ctrl-btn small-btn medium-btn" title="share-small-btn" @click="openSharing">
                    <i class="icon material-icons">
                      share
                    </i>
                  </a>
                  <a v-show="!isMe" :class="{'loading': followingUpdate}"
                     class="mk-btn mk-btn--secondary ctrl-btn large-btn" title="save-large-btn"
                     @click="toggleFollowing"
                  >
                    <template v-if="isFollowing">
                      <i class="material-icons">
                        favorite
                      </i>
                      <span>{{ $t('PLAYLIST.SAVED') }}</span>
                    </template>
                    <template v-else-if="!isFollowing">
                      <i class="material-icons">
                        favorite_border
                      </i>
                      <span>{{ $t('PLAYLIST.SAVE') }}</span>
                    </template>
                  </a>
                  <a class="mk-btn mk-btn--secondary ctrl-btn large-btn" title="share-large-btn" @click="openSharing">
                    <i class="material-icons">
                      share
                    </i>
                    <span>{{ $t('COMMON.SHARE') }}</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div v-if="playlist" class="description" v-html="displayLocaleDescription(playlist)">
            <!-- {{ displayLocaleDescription(playlist) }} -->
          </div>
          <PlaylistTable :playlist="playlist" @remove="removeTrackFromPlaylist" @update="resortTrackOrder" />
          <PlaylistEditModal :open="editPlaylist" :playlist="copiedPlaylist" @close="closeEditing" />
          <PlaylistShareModal
            :open="sharePlaylist"
            :playlist="playlist"
            :title="title"
            :subtitle="subtitle"
            :cover="cover"
            @close="sharePlaylist = false"
          />
          <PlaylistGoLoginModal :open="needToLogin" @close="needToLogin = false" />
        </div>
        <div v-else-if="!loaded" class="container--loader">
          <Loader />
        </div>
        <div v-else class="container--empty">
          <img src="@/assets/images/img-404/img-404.svg" alt="empty content" title="empty content">
        </div>
      </Transition>
    </div>
    <div class="pgnnp-footer">
      <Paganiniplus />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { displayLocaleTitle, displayLocaleDescription } from '@/assets/helpers/PlaylistHelper'
import { displayTrackName, displayComposerName, displayPerformerName } from '@/assets/helpers/TrackHelper.js'
import Loader from '@/components/common/Loader'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import Paganiniplus from '@/components/tsp/Paganiniplus'

import PlaylistTable from '@/components/playlist/PlaylistTable'
import PlaylistEditModal from '@/components/playlist/PlaylistEditModal'
import PlaylistShareModal from '@/components/playlist/PlaylistShareModal'
import PlaylistGoLoginModal from '@/components/playlist/PlaylistGoLoginModal'
import PlaylistService from '@/assets/services/PlaylistService'
import { getLocalStorage, getCookie } from '@/assets/helpers/UtilHelper'
import ImgLoader from '@/directives/ImgLoader'
import PlayerStaticPlayBtn from '@/components/player/PlayerStaticPlayBtn'

export default {
  name: 'PlaylistById',
  components: {
    Loader,
    Breadcrumbs,
    Paganiniplus,
    PlaylistTable,
    PlaylistEditModal,
    PlaylistShareModal,
    PlaylistGoLoginModal,
    PlayerStaticPlayBtn
  },
  directives: {
    ImgLoader
  },
  props: {},
  data() {
    return {
      loaded: false,
      rewriting: false,
      //
      editPlaylist: false,
      sharePlaylist: false,
      needToLogin: false,
      playlist: undefined,
      copiedPlaylist: undefined, // for edit
      followingUpdate: false
    }
  },
  metaInfo() {
    return {
      title: this.title,
      meta: [
        ...this.$route.meta.metaInfo.meta,
        { vmid: 'name', name: 'name', content: this.title },
        { vmid: 'description', name: 'description', content: this.description },
        { vmid: 'keywords', name: 'keywords', content: this.keywords },
        { vmid: 'image', name: 'image', content: this.cover },
        { vmid: 'og:title', name: 'og:title', content: this.title },
        { vmid: 'og:description', name: 'og:description', content: this.description },
        { vmid: 'og:image', name: 'og:image', content: this.cover },
        { vmid: 'og:url', name: 'og:url', content: this.canonicalURL },
        { vmid: 'og:audio:type', property: 'og:audio:type', content: 'audio/mpeg' },
        { vmid: 'og:audio', property: 'og:audio', content: this.track ? this.track.file_link : '' }
      ],
      link: [
        { vmid: 'canonical', rel: 'canonical', href: this.canonicalURL },
        { vmid: 'image_src', rel: 'image_src', href: this.cover }
      ],
      __dangerouslyDisableSanitizers: ['script'],
      script: [{ innerHTML: JSON.stringify(this.structuredData), type: 'application/ld+json' }]
    }
  },
  computed: {
    ...mapState({
      clientWidth: state => state.clientWidth,
      profile: state => state.user.profile,
      userPlaylist: state => state.user.playlist,
      userFollowing: state => state.user.following,
      initialClick: state => state.player.initialClick,
      display: state => state.player.display,
      random: state => state.player.random,
      paused: state => state.player.paused,
      currentTime: state => state.player.currentTime,
      queue: state => state.player.queue,
      pgnnpAudioMode: state => state.player.pgnnpAudioMode,
      isAnyPgnnpAudio: state => state.player.isAnyPgnnpAudio
    }),
    ...mapGetters(['isAuthenticated']),
    isMe() {
      return this.playlist && this.profile ? this.playlist['user_id'] === this.profile.id : false
    },
    isFollowing() {
      return this.playlist && this.userFollowing
        ? this.userFollowing.map(item => item.id).indexOf(this.playlist.id) > -1
        : false
    },
    isOfficialPlaylist() {
      return this.playlist && this.playlist['official_playlist_id'] && this.playlist['official_playlist']
    },
    coverBackgroundImage() {
      return this.playlist && this.playlist['file_link']
        ? { backgroundImage: `url(${this.playlist['file_link']}` }
        : {} // default img in scss
    },
    breadcrumbs() {
      let breadcrumbsAry = []
      const indexBreadcrumbs = { routeName: 'index', localeLabel: 'LISTEN.TITLE', redirectPath: '' }
      breadcrumbsAry.push(indexBreadcrumbs)
      if (this.playlist && this.isOfficialPlaylist) {
        // Just displaying owner name, as same as a member's playlist
        // since many to many scenarios relation can't mapping to breadcrumbs
        const scenarioBreadcrumbs = {
          routeName: 'memberPage',
          routeParams: {
            id: this.playlist.user.id
          },
          localeLabel: this.playlist.user.name || 'MEMBER.TITLE',
          redirectPath: ''
        }
        breadcrumbsAry.push(scenarioBreadcrumbs)
      } else if (this.playlist && this.playlist.user && this.playlist.user.id) {
        const scenarioBreadcrumbs = {
          routeName: 'memberPage',
          routeParams: {
            id: this.playlist.user.id
          },
          localeLabel: this.playlist.user.name || 'MEMBER.TITLE',
          redirectPath: ''
        }
        breadcrumbsAry.push(scenarioBreadcrumbs)
      }
      if (this.playlist) {
        const playlistBreadcrumbs = {
          routeName: '', // No router link at last breadcrumb
          localeLabel: this.displayLocaleTitle(this.playlist) || 'PLAYLIST.TITLE',
          redirectPath: ''
        }
        breadcrumbsAry.push(playlistBreadcrumbs)
      }
      return breadcrumbsAry
    },
    // default meta data, update after fetched article
    title() {
      const titlePostfix = `${this.$t('LISTEN.TITLE')} | MUZIK AIR`
      if (this.playlist && this.playlist.id) {
        const localeTitle = this.displayLocaleTitle(this.playlist)
        return localeTitle ? `${localeTitle} | ${titlePostfix}` : `${this.$t('PLAYLIST.TITLE')} | ${titlePostfix}`
      } else {
        return `${this.$t('PLAYLIST.TITLE')} | ${titlePostfix}`
      }
    },
    subtitle() {
      return this.playlist ? this.$t('PLAYLIST.CREATED_BY', { member: this.playlist.user.name }) : ''
    },
    description() {
      if (this.playlist && this.playlist.id) {
        const localeDescription = this.displayLocaleDescription(this.playlist)
        return localeDescription.slice(0, 200) || this.displayLocaleTitle(this.playlist) || this.$t('PLAYLIST.TITLE')
      } else {
        return this.$t('PLAYLIST.TITLE')
      }
    },
    keywords() {
      let keywordsAry = []
      keywordsAry.push(this.$t('PLAYLIST.TITLE'))
      keywordsAry.push(this.displayLocaleTitle(this.playlist))
      if (this.playlist && this.playlist.tracks) {
        this.playlist.tracks.forEach(track => {
          keywordsAry.push(this.displayTrackName(track))
          keywordsAry.push(this.displayComposerName(track.movement.work.people))
          track.performers.forEach(perfermer => {
            keywordsAry.push(this.displayPerformerName(perfermer.people))
          })
        })
      }
      return keywordsAry.join(', ')
    },
    cover() {
      return this.playlist && this.playlist['file_link']
      // return this.playlist && this.playlist['file_link']
      //   ? this.playlist['file_link']
      //   : `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/img/cover-social/listen.png`
    },
    canonicalURL() {
      return this.playlist && this.playlist.id
        ? `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/playlists/${this.playlist.id}`
        : `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}`
    },
    structuredData() {
      const structuredData = []
      let breadcrumbStructuredData = {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [{
          '@type': 'ListItem',
          'position': 1,
          'item': {
            '@id': `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/`,
            'name': this.$t('LISTEN.TITLE')
          }
        }]
      }

      if (this.playlist) {
        if (this.playlist.user) {
          breadcrumbStructuredData.itemListElement.push({
            '@type': 'ListItem',
            'position': 2,
            'item': {
              '@id': `${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/member/${this.playlist.user.id}`,
              'name': this.playlist.user.name
            }
          })
        }

        // if (this.playlist.official_playlist && this.playlist.official_playlist.scenario) {
        //   this.playlist.official_playlist.scenario.forEach(scenario => {
        //     breadcrumbStructuredData.itemListElement.push({
        //       '@type': 'ListItem',
        //       'position': 3,
        //       'item': {
        //         '@id': `${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/member/${scenario.id}`,
        //         'name': scenario.id
        //       }
        //     })
        //   })
        // }
        let playlistStructuredData = {
          '@context': 'http://schema.org',
          '@type': 'MusicPlaylist',
          'image': this.cover,
          'name': this.title,
          'url': this.canonicalURL,
          'description': this.description,
          'numTracks': 0,
          'track': []
        }
        if (this.playlist.tracks) {
          playlistStructuredData.numTracks = this.playlist.tracks.length
          this.playlist.tracks.forEach(track => {
            playlistStructuredData.track.push({
              '@type': 'MusicRecording',
              'name': this.displayTrackName(track),
              'duration': track.duration,
              'isrcCode': track.isrc,
              'url': `https://${process.env.VUE_APP_MUZIK_LISTEN}.${process.env.VUE_APP_MUZIK_DOMAIN}/${this.$i18n.localePath()}/tracks/${track.id}`
            })
          })
        }
        //
        structuredData.push(playlistStructuredData)
      }
      structuredData.push(breadcrumbStructuredData)
      return structuredData
    }
  },
  watch: {
    $route(to, from) {
      // Reacting to Params Changes: https://router.vuejs.org/guide/essentials/dynamic-matching.html
      if (to.params.id !== from.params.id && !this.rewriting) {
        this.initPlaylistPage()
      } else {
        // Pass rewrite routing.
        this.rewriting = false
      }
    }
  },
  created() {},
  mounted() {
    this.initPlaylistPage()
  },
  updated() {},
  beforeDestroy() {},
  methods: {
    displayLocaleTitle,
    displayLocaleDescription,
    displayTrackName,
    displayComposerName,
    displayPerformerName,
    getCookie,
    parseRewriteURL(unparsedIdStr) {
      const MuzikListenV4Reg = /^[A-F\d]{8}-[A-F\d]{4}-4[A-F\d]{3}-[89AB][A-F\d]{3}-[A-F\d]{12}/gi
      const MuzikListenV3Reg = /^[A-Z\d]{6}/gi
      let parsedIdStr = ''
      if (MuzikListenV4Reg.test(unparsedIdStr)) {
        parsedIdStr = unparsedIdStr.match(MuzikListenV4Reg)[0] // MUZIK LISTEN v4 (UUID.v4) id
      } else if (MuzikListenV3Reg.test(unparsedIdStr)) {
        parsedIdStr = unparsedIdStr.match(MuzikListenV3Reg)[0] // MUZIK LISTEN v3 id format
      } else {
        parsedIdStr = unparsedIdStr
      }
      return parsedIdStr
    },
    async initPlaylistPage() {
      this.loaded = false
      this.playlist = undefined
      const unparsedIdStr = this.$route.params.id.replace('.html', '') // external share link
      const playlistId = this.parseRewriteURL(unparsedIdStr)
      const playlistRewrite = unparsedIdStr.replace(playlistId, '')
      const playlist = await PlaylistService.getPlaylistById(playlistId)
      if (playlist) {
        this.playlist = playlist
        this.loaded = true

        // Re-write URL
        const titleText = this.displayLocaleTitle(this.playlist)
        let rewriteURLId = titleText
          ? `${this.playlist.id}-${titleText}`.replace(' ', '-')
          : this.playlist.id
        if (rewriteURLId && rewriteURLId !== unparsedIdStr) {
          this.rewriting = true
          this.$router.replace({
            params: { id: rewriteURLId },
            query: this.$route.query // pass original query
          })
        }

        // Update player default queue
        if (!this.initialClick && this.playlist.tracks && this.playlist.tracks.length > 0) {
          // still not click once, auto set queue and player to current playlist
          this.$store.commit('TOGGLE_PLAYER_RANDOM', false)
          this.$store.commit('RESET_QUEUE')
          this.$store.commit('SET_PLAYLIST_TO_QUEUE', this.playlist.tracks)
          this.$store.commit('SET_PLAYER_INDEX', 0)
        }
      } else {
        if (playlistRewrite) {
          this.loaded = true
          const queryKeyword = playlistRewrite.split('-').join(' ')
          this.$router.push({
            name: 'search',
            query: {
              type: 'playlist',
              q: queryKeyword
            }
          })
        } else {
          this.$store.commit('ALERT_MESSAGE', { message: 'COMMON.NOT_FOUND', type: 'error' })
          this.$router.push(`/${this.$i18n.localePath()}`)
        }
      }
    },
    toggleFollowing() {
      if (!this.followingUpdate) {
        if (this.isAuthenticated) {
          if (this.isFollowing) {
            this.removeFromFollowing()
          } else {
            this.addToFollowing()
          }
        } else {
          this.showNeedToLogin()
        }
      }
    },
    async addToFollowing() {
      if (!this.followingUpdate) {
        this.followingUpdate = true
        const params = [this.playlist.id]
        const res = await PlaylistService.addPlaylistFollowing(params)
        if (res) {
          this.$store.commit('ALERT_MESSAGE', { message: 'PLAYLIST.SAVE_SUCCESS', type: 'success' })
          await this.$store.dispatch('UPDATE_USER_FOLLOWING')
          this.followingUpdate = false
        } else {
          this.$store.commit('ALERT_MESSAGE', { message: 'PLAYLIST.SAVE_ERROR', type: 'error' })
        }
      }
    },
    async removeFromFollowing() {
      if (!this.followingUpdate) {
        this.followingUpdate = true
        const params = [this.playlist.id]
        const res = await PlaylistService.deletePlaylistFollowing(params)
        if (res) {
          this.$store.commit('ALERT_MESSAGE', { message: 'PLAYLIST.REMOVE_SUCCESS', type: 'success' })
          await this.$store.dispatch('UPDATE_USER_FOLLOWING')
          this.followingUpdate = false
        } else {
          this.$store.commit('ALERT_MESSAGE', { message: 'PLAYLIST.REMOVE_ERROR', type: 'error' })
        }
      }
    },
    async resortTrackOrder(tracks) {
      const tokenInLocalStorage = getLocalStorage('token')
      let newPalylist = null
      let tracksArray = {}
      tracksArray.tracks = []
      tracks.forEach((track, index) => {
        tracksArray.tracks.push(track.id)
      })
      newPalylist = await PlaylistService.sortTracksInPlaylist(tokenInLocalStorage, this.playlist.id, tracksArray)
      if (newPalylist) {
        this.playlist.tracks = tracks
        this.$store.commit('ALERT_MESSAGE', { message: 'COMMON.SAVE_CHANGE', type: 'success' })
      } else {
        //
      }
    },
    async convertToFormData(dataArray) {
      const bodyFormData = new FormData()
      Object.keys(dataArray).forEach(index => {
        if (Array.isArray(dataArray[index])) {
          dataArray[index].forEach((item, index2) => {
            if (typeof item === 'object') {
              Object.keys(item).forEach(data => {
                bodyFormData.append(`${index}[${index2}][${data}]`, item[data])
              })
            } else {
              bodyFormData.append(`${index}[]`, item)
            }
          })
        } else if (dataArray[index] !== null && typeof dataArray[index] === 'object') {
          bodyFormData.append(index, dataArray[index])
        } else {
          bodyFormData.append(index, dataArray[index])
        }
      })
      return bodyFormData
    },
    openSharing() {
      this.sharePlaylist = true
    },
    openEditing() {
      if (this.isAuthenticated && this.isMe) {
        this.copiedPlaylist = Object.assign({}, this.playlist)
        this.copiedPlaylist.tracks = []
        this.editPlaylist = true
      }
    },
    closeEditing(newPlaylist) {
      if (newPlaylist) {
        this.playlist['title'] = newPlaylist['title']
        this.playlist['file_id'] = newPlaylist['file_id']
        this.playlist['file_link'] = newPlaylist['file_link']
      } else {
        // cancel or error
      }
      this.editPlaylist = false
    },
    showNeedToLogin() {
      this.needToLogin = true
    },
    removeTrackFromPlaylist(trackId) {
      if (this.playlist && this.playlist.tracks) {
        const targetIndex = this.playlist.tracks.findIndex(pItem => pItem.id === trackId)
        this.playlist.tracks.splice(targetIndex, 1)
      }
    },
    setPlaylistToQueue() {
      const isFirstPlay = this.getCookie('player_play_count') === null
      if (isFirstPlay) {
        this.$store.commit('SET_TO_PREPEND', true)
        this.$store.dispatch('PGNNP_AUDIO_NEXT')
        this.$nextTick(() => {
          this.$store.commit('TOGGLE_PLAYER_PAUSED', false) // trigger auto play
        })
        if (!this.isAnyPgnnpAudio) {
          document.querySelector('#audioPlayer').play()
          document.querySelector('#audioPlayer').pause()
        }
      } else if (this.playlist && this.playlist.tracks && this.playlist.tracks.length > 0) {
        if (!this.initialClick) {
          // Trigger click-to-play policy
          document.querySelector('#audioPlayer').play()
          document.querySelector('#audioPlayer').pause()
        }
        this.$store.commit('TOGGLE_PLAYER_RANDOM', false)
        this.$store.commit('RESET_QUEUE')
        this.$store.commit('SET_PLAYLIST_TO_QUEUE', this.playlist.tracks)
        this.$store.commit('SET_PLAYER_INDEX', 0)
        // Increase playing count for previous track
        if (!this.paused || this.currentTime > 0) {
          this.$store.dispatch('INCREASE_PLAYING_COUNT')
        }
        if (!this.display) {
          this.$store.commit('TOGGLE_PLAYER_DISPLAY', true)
        }
        this.$nextTick(() => {
          this.$store.commit('TOGGLE_PLAYER_PAUSED', false) // trigger auto play
        })
        this.$store.commit('TOGGLE_PLAYER_PANEL', true)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
@import '@/assets/scss/component/button.scss';

@keyframes beat {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.6);
  }
  100% {
    transform: scale(1);
  }
}
.beat {
  // color: pink;
  animation-name: beat;
  animation-duration: 1s;
  animation-iteration-count: 2;
  // animation-iteration-count: infinite;
}
.playlist {
  .container--empty {
    margin: 24px auto;
    text-align: center;
    img {
      width: 100%;
      max-width: 561px;
    }
  }
  .container--loader {
    display: block;
    height: 200px;
    margin: 24px auto;
    text-align: center;
  }
  .container--playlist {
    min-height: 200px;
    margin: 24px 0;
    margin-bottom: 36px;
    font-size: 16px;
  }
  .icon-btn {
    display: inline-block;
    vertical-align: middle;
    width: 40px;
    height: 40px;
    line-height: 40px;
    color: $grey-7d;
    text-align: center;
    cursor: pointer;
    > .icon {
      vertical-align: middle;
    }
  }
  .info {
    margin-bottom: 40px;
    .cover {
      position: relative;
      width: 213px;
      height: 190px;
      overflow: hidden;
      float: left;

      border-radius: 12px;
      background-color: $grey-ec;
      background-image: url('/img/img-default/img-playlist-default-l.png');
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
      img {
        width: 213px;
        height: 190px;
        border-radius: 9px;
      }
      margin-right: 36px;
    }
    .box {
      position: relative;
      overflow: hidden;
      .subject {
        height: 40px;
        margin-bottom: 16px;
        .title {
          position: relative;
          overflow: hidden;
          margin: 0;
          font-size: 24px;
          font-weight: 500;
          line-height: 1.63;
          color: $grey-50;

          white-space: nowrap;
          // overflow: hidden; // exist by BFC
          text-overflow: ellipsis;
        }
        .icon-btn {
          // add on
          position: relative;
          overflow: hidden;
          float: right;
          margin-left: 8px;
        }
      }
      .created {
        min-height: 36px;
        margin-bottom: 16px;
        .avatar,
        .text {
          position: relative;
          overflow: hidden;
        }
        .avatar {
          float: left;
          width: 36px;
          width: 36px;
          margin-right: 8px;
          border-radius: 50%;
          img {
            width: 36px;
            height: 36px;
            border-radius: 50%;
          }
        }
        .text {
          font-size: 20px;
          line-height: 36px;
          letter-spacing: -0.1px;
          color: $grey-50;

          white-space: nowrap;
          // overflow: hidden; // exist by BFC
          text-overflow: ellipsis;
        }
      }
      .control {
        min-height: 84px;
        position: relative;
        user-select: none;

        .play-btn {
          position: relative;
          bottom: 0;
          left: 0;
          width: 77px;
          height: 77px;
          cursor: pointer;
          // img {
          //   float: left;
          //   width: 77px;
          //   height: 77px;
          // }
          &.disabled{
            cursor: not-allowed;
          }
        }
        .btn-list {
          position: absolute;
          bottom: 0;
          right: 0;
          > a {
            margin-left: 8px;
            > i {
              margin-right: 8px;
            }
          }
        }
      }
    }
    &:after {
      content: '';
      display: block;
      clear: both;
    }
  }
  .description {
    position: relative;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    color: $grey-7d;
    margin-bottom: 24px;
    /deep/ a {
      text-decoration: none;
      color: $link-hover;
      &:hover {
        text-decoration: underline;
      }
    }
  }
}

i.loading-link {
  &.loading {
    &:before {
      width: 26px;
      height: 26px;
      border: 2px solid $theme-yellow;
      border-top-color: #f3f3f3;
      animation: rotating 1s infinite;
    }
  }
}

.pgnnp-header {
  margin-top: 24px;
}

.pgnnp-footer {
  margin-bottom: 36px;
}

@media only screen and (min-width: 1440px) {
  .box .ctrl-btn:not(.large-btn) {
    display: none;
  }
}
@media only screen and (min-width: 1024px) and (max-width: 1439.98px) {
  .box .ctrl-btn:not(.large-btn) {
    display: none;
  }
}
@media only screen and (min-width: 768px) and (max-width: 1023.98px) {
  .box .ctrl-btn:not(.medium-btn) {
    display: none;
  }
  .playlist {
    .container--playlist {
      margin: 20px 0px;
    }
    .info {
      .cover {
        width: 210px;
        height: 187px;
        img {
          width: 210px;
          height: 187px;
        }
      }
    }
    .description {
      font-size: 14px;
      font-weight: 500;
      color: $grey-9b;
    }
  }
}
@media only screen and (max-width: 767.98px) {
  .box .ctrl-btn:not(.small-btn) {
    display: none;
  }

  .playlist {
    .container--playlist {
      margin: 20px 0px;
    }
    .info {
      .cover {
        width: 136px;
        height: 120px;
        float: unset;
        text-align: center;
        margin: 0 auto;
        img {
          width: 136px;
          height: 120px;
        }
      }
      .box {
        .subject,
        .created,
        .control {
          width: 100%;
          text-align: center;
        }
        .subject {
          margin: 16px 0;
        }
        .created {
          margin: 0;
          > .avatar {
            float: unset; // reset
          }
          > .avatar,
          > .text {
            display: inline-block;
            vertical-align: middle;
            white-space: normal;
          }
        }
        .control {
          > .play-btn {
            display: block;
            margin: 24px auto;
            width: 50px;
            height: 50px;
            // img {
            //   width: 50px;
            //   height: 50px;
            // }
          }
          > .btn-list {
            display: block;
            text-align: center;
            position: relative; // clean absolute
          }
        }
      }
    }
    .description {
      font-size: 14px;
      font-weight: 500;
      color: $grey-9b;
    }
  }
  .pgnnp-footer {
    margin-bottom: 36px;
  }
}

</style>
